import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, Mail, MapPin, Globe, Star, CheckCircle, ArrowRight, Monitor, Smartphone, Search, Settings, Shield, Zap } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { mockData } from '../data/mock';
import axios from 'axios';
import patricePhoto from '../assets/images/ceoPhoto.jpg'
import mariemPhoto from '../assets/images/photoMariem.png'
import Footer from './Footer';
import Header from './Header';
import { blogPosts } from '../data/blogPosts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { toast } = useToast();
const [formData, setFormData] = useState({
  name: "",
  email: "",
  telephone: "",
  message: "",
  type: "contact",
});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;

    window.requestAnimationFrame(() => {
      const section = document.getElementById(id);
      section?.scrollIntoView({ behavior: "smooth" });
    });
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

   const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e) => {
  e.preventDefault();

  // Validation front (miroir du backend)
  if (formData.name.trim().length < 2) {
    toast({
      title: "Nom invalide",
      description: "Le nom doit contenir au moins 2 caractères.",
      variant: "destructive",
    });
    return;
  }

  if (formData.message.trim().length < 10) {
    toast({
      title: "Message trop court",
      description: "Le message doit contenir au moins 10 caractères.",
      variant: "destructive",
    });
    return;
  }

  setIsSubmitting(true);
  setError("");

  try {
    const response = await axios.post(`${API}/contact`, {
      name: formData.name.trim(),
      email: formData.email.trim(),
      telephone: formData.telephone?.trim() || null,
      message: formData.message.trim(),
    });

    if (response.data.success) {
      toast({
        title: "Message envoyé !",
        description: response.data.message,
      });

      setFormData({
        name: "",
        email: "",
        telephone: "",
        message: "",
      });
    }
  } catch (error) {
    console.error("Erreur envoi formulaire:", error);

    let message = "Une erreur est survenue. Veuillez réessayer.";

    if (error.response) {
      const detail = error.response?.data?.detail;

      // Cas erreurs Pydantic → tableau d'objets
      if (Array.isArray(detail)) {
        message = detail.map((item) => item.msg).join(", ");
      }
      // Cas erreur custom backend
      else if (typeof detail === "string") {
        message = detail;
      }
    }

    setError(message);

    toast({
      title: "Erreur",
      description: message,
      variant: "destructive",
    });
  } finally {
    setIsSubmitting(false);
  }
};


const scrollToSection = (id) => {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth' });
  }
};

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-page)' }}>
      <Helmet>
        <title>Fold Web Agency</title>
        <meta
          name="description"
          content="Fold Web Agency - Création de sites WordPress, sites sur mesure et services web personnalisés."
        />
        <link rel="canonical" href="https://fold-web-agency.fr/" />
        <meta property="og:title" content="Fold Web Agency" />
        <meta
          property="og:description"
          content="Création de sites WordPress, sites sur mesure et services web personnalisés."
        />
        <meta property="og:url" content="https://fold-web-agency.fr/" />
      </Helmet>

      {/* Header Navigation */}
      <Header scrollToSection={scrollToSection}/>
      {/* Hero Section */}
      <section id="accueil" className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Globe className="w-4 h-4" />
           Donnez vie à votre présence
          </div>
          
          <h1 className="heading-hero mb-6">
            Votre présence en ligne,<br />
            <span className="text-orange-500">notre expertise</span>
          </h1>
          
          <p className="body-large hero-subtitle text-center">
           Création de sites vitrines modernes et performants pour une présence en ligne claire, professionnelle et efficace.
          </p>
          
          <div className="hero-cta-group">
            <button 
              onClick={() => scrollToSection('contact')} 
              className="btn-primary hover-scale"
            >
              Demander un devis gratuit
            </button>
            {/* <a 
              href="tel:+33123456789" 
              className="btn-secondary hover-scale"
            >
              <Phone className="w-4 h-4 mr-2" />
              Appeler maintenant
            </a> */}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Nos Services</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Des solutions complètes pour développer votre présence en ligne
            </p>
          </div>
          
          <div className="services-grid">
            {mockData.services.map((service, index) => (
              <Card key={index} className="service-card hover-lift">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                    {service.icon}
                  </div>
                  <CardTitle className="heading-3">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium text-center">
                    {service.description}
                  </CardDescription>
                  <ul className="mt-4 space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {service.path && (
                    <Link to={service.path} className="service-learn-more">
                      En savoir plus
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section alt">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Notre Processus en 4 Étapes</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Une méthode éprouvée pour créer votre site vitrine parfait
            </p>
          </div>
          
          <div className="process-grid">
            {mockData.processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{index + 1}</div>
                <h3 className="heading-3 mb-2">{step.title}</h3>
                <p className="body-medium">{step.description}</p>
                <p className="caption mt-2">{step.duration}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Nos Réalisations</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Découvrez quelques-uns des sites vitrines que nous avons créés
            </p>
          </div>
          
          <div className="portfolio-grid">
            {mockData.portfolioItems.map((item, index) => (
              <Card key={index} className="portfolio-card hover-lift">
                <div className="portfolio-image">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                    <Monitor className="w-12 h-12 text-gray-400" />
                  </div>
                </div>
                <CardContent className="portfolio-content">
                  <CardTitle className="heading-3 mb-2">
                    <Link to={`/portfolio/${item.slug}`} className="portfolio-title-link">
                      {item.title}
                    </Link>
                  </CardTitle>
                  <CardDescription className="body-small mb-3">{item.description}</CardDescription>
                  <div className="flex flex-wrap gap-1">
                    {item.technologies.map((tech, idx) => (
                      <span key={idx} className="caption bg-gray-100 px-2 py-1 rounded">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <Link to={`/portfolio/${item.slug}`} className="service-learn-more">
                    Voir le cas client
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="blog" className="section alt">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Conseils web et SEO</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Des guides pratiques pour mieux comprendre la création de site,
              WordPress et le référencement naturel.
            </p>
          </div>

          <div className="blog-grid">
            {blogPosts.slice(0, 3).map((post) => (
              <article className="blog-card" key={post.slug}>
                <div className="blog-card-meta">
                  <span>{post.category}</span>
                  <span>{post.readingTime}</span>
                </div>
                <h3 className="heading-3">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h3>
                <p className="body-medium">{post.excerpt}</p>
                <Link to={`/blog/${post.slug}`} className="service-learn-more">
                  Lire l'article
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <section id="tarifs" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Nos Tarifs</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Des formules adaptées à tous les budgets avec <strong>25% de réduction</strong> pour nos premiers clients
            </p>
          </div>
          
          {/* Main Pricing Table */}
          {/* <div className="overflow-x-auto">
            <div className="pricing-table min-w-[800px]">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="heading-3 text-left p-4">Formule</th>
                    <th className="heading-3 text-center p-4">Prix Standard</th>
                    <th className="heading-3 text-center p-4">Prix de Lancement</th>
                    <th className="heading-3 text-left p-4">Inclus</th>
                  </tr>
                </thead>
                <tbody>
                  {mockData.pricingPlans.map((plan, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="p-4">
                        <div className="heading-3 mb-2">{plan.name}</div>
                        <div className="caption text-gray-600">{plan.description}</div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="price-original text-lg">{plan.originalPrice}€</div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="price-launch text-xl font-bold">{plan.launchPrice}€</div>
                        <div className="caption text-orange-600 font-medium">-25% réduction</div>
                      </td>
                      <td className="p-4">
                        <ul className="space-y-2">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="body-small flex items-start">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div> */}

          {/* Additional Options */}
          {/* <div className="mt-12">
            <h3 className="heading-2 text-center mb-8">Options Supplémentaires</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockData.additionalOptions.map((option, index) => (
                <Card key={index} className="text-center p-6 hover-lift">
                  <CardHeader className="pb-4">
                    <CardTitle className="heading-3 mb-2">{option.name}</CardTitle>
                    <CardContent className="p-0">
                      <p className="body-small text-gray-600 mb-4">{option.description}</p>
                      <div className="price-launch text-xl font-bold">{option.price}</div>
                    </CardContent>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div> */}

          {/* CTA */}
          {/* <div className="text-center mt-12">
            <p className="body-large mb-6 max-w-2xl mx-auto">
              <strong>Offre de lancement limitée !</strong> Profitez de 25% de réduction sur toutes nos formules. 
              Une occasion unique de créer votre présence en ligne à prix réduit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => scrollToSection('contact')}
                className="btn-primary hover-scale px-8"
              >
                Profiter de l'offre
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a 
                href="tel:+33123456789" 
                className="btn-secondary hover-scale px-8"
              >
                Discuter par téléphone
              </a>
            </div>
          </div>
        </div> */}
      {/* </section> */} 

      {/* FAQ Section */}
      <section className="section alt" id="faq">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Questions Fréquentes</h2>
          </div>
          
          <div className="faq-container">
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg px-6">
                  <AccordionTrigger className="heading-3 text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="body-medium pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* About Team Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Qui sommes-nous ?</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Une agence portée par une vision humaine, technique et orientée résultats.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            
            {/* === Patrice Tord === */}
            <div className="flex flex-col items-center text-center">
              <img
                src={patricePhoto}
                alt="Patrice Tord – Président et CEO"
                className="w-56 h-56 object-cover rounded-full shadow-lg mb-6"
              />

              <h3 className="heading-3 mb-1">Patrice Tord</h3>
              <p className="text-orange-500 font-semibold mb-4">
                Président · Co-fondateur · CEO
              </p>

              <p className="body-medium mb-3">
                Entrepreneur passionné par le digital et la stratégie, j'accompagne
                les entreprises dans la création de sites clairs, efficaces et orientés
                conversion.
              </p>

              <p className="body-medium">
                Chaque projet est pensé comme un partenariat durable, avec une approche
                humaine, transparente et orientée résultats.
              </p>
            </div>

            {/* === Mariem Aloui === */}
            <div className="flex flex-col items-center text-center">
              <img
                src={mariemPhoto}
                alt="Mariem Aloui – Responsable technique"
                className="w-56 h-56 object-cover rounded-full shadow-lg mb-6"
              />

              <h3 className="heading-3 mb-1">Mariem Aloui</h3>
              <p className="text-orange-500 font-semibold mb-4">
                Directrice technique · Co-fondatrice
              </p>

              <p className="body-medium mb-3">
                Développeuse et architecte technique, Je suis la garante de la qualité,
                de la performance et de la sécurité des solutions mises en place.
              </p>

              <p className="body-medium">
                Je veille à concevoir des sites modernes, fiables et évolutifs, pensés
                pour durer et s’adapter aux besoins métiers.
              </p>
            </div>

          </div>
        </div>
      </section>



      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Contactez-nous</h2>
            <p className="body-large text-center max-w-2xl mx-auto">
              Prêt à lancer votre projet ? Parlons-en !
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="contact-form">
              <h3 className="heading-2 mb-6">Demande de devis gratuit</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Nom complet *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Téléphone</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-input form-textarea"
                    placeholder="Décrivez votre projet..."
                    required
                  ></textarea>
                </div>
                
                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                  {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="contact-form">
                <CardHeader>
                  <CardTitle className="heading-2">Informations de contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    {/* <Phone className="w-5 h-5 text-orange-500" />
                    <span className="body-medium">+33 1 23 45 67 89</span> */}
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-orange-500" />
                    <span className="body-medium">contact@fold-web-agency.fr</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    <span className="body-medium">Collectifs d'indépendants : Bordeaux - Paris</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="contact-form">
                <CardHeader>
                  <CardTitle className="heading-3">Réponse rapide garantie</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center body-small">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Réponse sous 24h maximum
                    </li>
                    <li className="flex items-center body-small">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Devis détaillé gratuit
                    </li>
                    <li className="flex items-center body-small">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Conseil personnalisé
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <div className="flex space-x-4">
                {/* <a href="https://wa.me/#" className="btn-secondary flex-1 text-center">
                  WhatsApp
                </a>
                <a href="tel:+#" className="btn-primary flex-1 text-center">
                  Appeler
                </a> */}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default HomePage;
